/**
 * useVocalPacks Hook
 * Fetches all vocal pack profiles from the API with fallback to static data.
 * Returns { data, loading, error, refetch } where data is Record<string, VocalPack>.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { VocalPack } from '@/types';
import { VOCAL_PACKS } from '@/vocalPacks';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

interface UseVocalPacksReturn {
  data: Record<string, VocalPack>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useVocalPacks(): UseVocalPacksReturn {
  const [data, setData] = useState<Record<string, VocalPack>>(VOCAL_PACKS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf(`${API_ENDPOINTS.vocalPacks.list}?full=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.data && Object.keys(json.data).length > 0) {
        setData(json.data);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch vocal packs';
      setError(msg);
      console.error('[useVocalPacks] Fetch failed, using static fallback:', msg);
      // Keep static data as fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      refetch();
    }
  }, [refetch]);

  return { data, loading, error, refetch };
}
